import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema, type LoginRequest } from '@formacion-docente/schemas';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface Props {
  onSubmit: (values: LoginRequest) => void;
  loading?: boolean;
  errorMessage?: string;
}

/**
 * Form de login (email + password). Validación Zod + RHF. El componente
 * padre controla `onSubmit`, loading y mensaje de error.
 *
 * En modo dev (import.meta.env.DEV) autocompleta credenciales si existen
 * en `import.meta.env.VITE_DEV_LOGIN_EMAIL` / `VITE_DEV_LOGIN_PASSWORD`
 * — utiles para no tipear a mano durante desarrollo.
 */
export function LoginForm({ onSubmit, loading, errorMessage }: Props) {
  const devEmail = import.meta.env.VITE_DEV_LOGIN_EMAIL ?? '';
  const devPassword = import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? '';
  const isDev = import.meta.env.DEV;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: {
      email: isDev ? devEmail : '',
      password: isDev ? devPassword : '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3" noValidate>
      <div>
        <label htmlFor="email" className="mb-1 block text-sm font-medium">
          Email, usuario o cédula
        </label>
        <Input
          id="email"
          type="text"
          autoComplete="username"
          aria-invalid={Boolean(errors.email)}
          {...register('email')}
        />
        {errors.email && (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="password" className="mb-1 block text-sm font-medium">
          Contraseña
        </label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          aria-invalid={Boolean(errors.password)}
          {...register('password')}
        />
        {errors.password && (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {errors.password.message}
          </p>
        )}
      </div>

      {errorMessage && (
        <p role="alert" className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      <Button type="submit" disabled={loading || isSubmitting} className="mt-2 w-full">
        {loading ? 'Ingresando…' : 'Iniciar sesión'}
      </Button>
    </form>
  );
}